import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LEADERSHIP, VALUES, SUB_BRANDS } from "../data/content";
import { PAGE_SEO } from "../data/seoContent";
import SEO, { breadcrumbSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

const ACHIEVEMENTS = [
  { n: "UK-wide", l: "National operating footprint" },
  { n: "6", l: "Specialist divisions" },
  { n: "5,000+", l: "Annual inspections delivered" },
  { n: "ISO Accredited", l: "Aligned management systems" },
];

export default function About() {
  const crumbs = [{ name: "Home", path: "/" }, { name: "About", path: "/about" }];
  return (
    <div data-testid="page-about">
      <SEO {...PAGE_SEO.about} schema={breadcrumbSchema(crumbs)} />
      <section className="bg-[#0A0F1C] text-white pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-8"><Breadcrumb items={crumbs} theme="dark" /></div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">About Oxford Environmental Consultants</p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            A multi-disciplinary compliance group, built for <span className="gs-gradient-text">duty holders.</span>
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
            Oxford Environmental Consultants Limited (OEC) is the UK's one-stop property compliance consultancy. We deliver every statutory compliance discipline — asbestos, fire safety, water hygiene, damp &amp; mould, building safety and remedial works — under a single, accountable consultancy umbrella. Specialist work is delivered directly under OEC, alongside our specialist trading brands Firesurv and Mouldsurv.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-px">
          {ACHIEVEMENTS.map((a, i) => (
            <div key={i} className="bg-[#F8FAFC] p-8 border-r border-b border-slate-200">
              <p className="font-display text-4xl font-bold text-slate-900">{a.n}</p>
              <p className="text-xs text-slate-500 mt-2 uppercase tracking-wider">{a.l}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Our story</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">One group. Deep specialism.</h2>
          </div>
          <div className="lg:col-span-7 text-slate-600 text-lg leading-relaxed space-y-5">
            <p>
              Founded to solve a persistent problem for property managers — the overhead of coordinating multiple single-discipline consultants — Oxford Environmental Consultants consolidates asbestos, fire, damp and wider building safety under one roof, without diluting technical depth.
            </p>
            <p>
              Each of our discipline-led divisions — asbestos and water hygiene delivered directly under <strong>Oxford Environmental Consultants</strong>, plus our specialist trading brands <strong>Firesurv</strong> (fire safety) and <strong>Mouldsurv</strong> (damp &amp; mould) — is led by senior specialists in its field. Together they share a common technical governance framework, aligned to ISO 9001, SMAS Worksafe and SSIP.
            </p>
            <p>
              The result is a consultancy that can scope and deliver a full compliance programme across a national portfolio, from a single point of accountability.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Trading brands</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">The OEC family.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.values(SUB_BRANDS).map((b) => (
              <a key={b.slug} href={b.website} target="_blank" rel="noreferrer" className="group bg-white border border-slate-200 hover:border-slate-900 transition-colors" data-testid={`about-brand-${b.slug}`}>
                <div className="h-48 flex items-center justify-center bg-white border-b border-slate-200 p-4">
                  <img src={b.logo} alt={b.name} loading="lazy" decoding="async" className="max-h-full max-w-full object-contain" style={{ width: '100%', height: '100%' }} />
                </div>
                <div className="p-6">
                  <p className="font-display text-xl font-semibold text-slate-900 mb-1">{b.name}</p>
                  <p className="text-sm text-slate-600">{b.tagline}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Leadership</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">The people behind the practice.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {LEADERSHIP.map((p) => (
              <div key={p.name} className="border border-slate-200 p-10 hover:border-slate-900 transition-colors">
                <div className="w-16 h-16 rounded-full gs-gradient flex items-center justify-center text-white font-display font-bold text-xl">
                  {p.name.split(" ").map(n => n[0]).join("")}
                </div>
                <p className="font-display text-2xl font-semibold text-slate-900 mt-6">{p.name}</p>
                <p className="text-sm text-[#1FA59A] font-semibold mt-1">{p.role}</p>
                <p className="text-slate-600 mt-4 leading-relaxed">{p.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Values</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">How we work.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white p-8 min-h-[200px]">
                <p className="font-display text-2xl font-semibold text-slate-900 mb-3">{v.title}</p>
                <p className="text-slate-600 leading-relaxed">{v.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0F1C] text-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-3xl lg:text-4xl font-bold max-w-xl">Work with a compliance partner who takes the work off your desk.</h2>
          <Link to="/contact" className="gs-gradient px-8 py-4 font-bold flex items-center gap-2 hover:opacity-90 w-fit" data-testid="about-cta">
            Speak to a consultant <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
